import React from 'react';
import { BadgeDollarSign, Plus, Loader2 } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Card, Chip, EmptyState, fmtDate, OFFER_STATUS_BADGES } from './drawerShared';

const OfferForm = ({ form, setForm, saving, onSubmit, onCancel, reissue }) => (
  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/70 p-4" data-testid="offer-form">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-1">
        <Label className="text-xs">Offer Date *</Label>
        <Input type="date" className="h-9 bg-white" value={form.offer_date} onChange={(e) => setForm((p) => ({ ...p, offer_date: e.target.value }))} data-testid="offer-date-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Expiry Date</Label>
        <Input type="date" className="h-9 bg-white" value={form.expiry_date} onChange={(e) => setForm((p) => ({ ...p, expiry_date: e.target.value }))} data-testid="offer-expiry-input" />
      </div>
      <div className="space-y-1 sm:col-span-2">
        <Label className="text-xs">Proposed Salary (USD, annual)</Label>
        <Input type="number" min="0" className="h-9 bg-white" value={form.salary} placeholder="e.g. 65000" onChange={(e) => setForm((p) => ({ ...p, salary: e.target.value }))} data-testid="offer-salary-input" />
      </div>
    </div>
    <div className="space-y-1">
      <Label className="text-xs">Notes</Label>
      <Textarea rows={2} className="bg-white" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
    </div>
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
      <Button size="sm" onClick={onSubmit} disabled={saving} data-testid="offer-submit-btn">
        {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} {reissue ? 'Re-issue Offer' : 'Issue Offer'}
      </Button>
    </div>
  </div>
);

export const OfferCard = ({ offer, canEdit, showForm, setShowForm, form, setForm, saving, onSubmit, onStatus }) => {
  const issueBtn = canEdit && !offer && (
    <Button size="sm" className="h-7 gap-1 border border-amber-200 bg-amber-50 text-xs font-semibold text-amber-800 shadow-none hover:bg-amber-100" onClick={() => setShowForm((v) => !v)} data-testid="drawer-create-offer-btn">
      <Plus className="h-3 w-3" /> Issue Offer
    </Button>
  );
  return (
    <Card title="Offer" icon={BadgeDollarSign} action={issueBtn} data-testid="drawer-offer-section">
      {showForm && <div className="mb-3"><OfferForm form={form} setForm={setForm} saving={saving} onSubmit={onSubmit} onCancel={() => setShowForm(false)} reissue={!!offer} /></div>}
      {offer ? (
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="offer-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="sf-heading text-lg font-bold tracking-tight text-slate-900">
              {offer.salary != null ? `$${Number(offer.salary).toLocaleString()} ${offer.currency || 'USD'}` : 'Salary not specified'}
            </span>
            <Chip tone={OFFER_STATUS_BADGES[offer.status] || ''} className="uppercase tracking-wide" data-testid="offer-status-badge">{offer.status}</Chip>
          </div>
          <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <div><dt className="font-bold uppercase tracking-[0.06em] text-slate-400">Offered</dt><dd className="mt-0.5 text-slate-700">{fmtDate(offer.offer_date)}</dd></div>
            <div><dt className="font-bold uppercase tracking-[0.06em] text-slate-400">Issued by</dt><dd className="mt-0.5 text-slate-700">{offer.offered_by_name || '—'}</dd></div>
            {offer.expiry_date && <div><dt className="font-bold uppercase tracking-[0.06em] text-slate-400">Expires</dt><dd className="mt-0.5 text-slate-700">{fmtDate(offer.expiry_date)}</dd></div>}
          </dl>
          {offer.notes && <p className="mt-3 rounded bg-slate-50 p-2 text-xs text-slate-600">{offer.notes}</p>}
          {canEdit && offer.status === 'pending' && (
            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
              <Button size="sm" className="h-7 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={() => onStatus('accepted')} data-testid="offer-accept-btn">Mark Accepted</Button>
              <Button size="sm" variant="outline" className="h-7 border-rose-200 text-xs text-rose-600 hover:bg-rose-50" onClick={() => onStatus('declined')} data-testid="offer-decline-btn">Declined</Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => onStatus('expired')} data-testid="offer-expire-btn">Expired</Button>
            </div>
          )}
          {canEdit && offer.status !== 'pending' && offer.status !== 'accepted' && !showForm && (
            <Button variant="outline" size="sm" className="mt-3 h-7 gap-1 text-xs" onClick={() => setShowForm(true)} data-testid="offer-reissue-btn">
              <Plus className="h-3 w-3" /> Re-issue Offer
            </Button>
          )}
        </div>
      ) : !showForm && (
        <EmptyState icon={BadgeDollarSign} title="No offer issued" hint="An accepted offer is required before a hired candidate can be converted to an employee." testId="offer-empty" />
      )}
    </Card>
  );
};
